/**
 * Social account/post metric display helpers (cas-c53f).
 *
 * The backend now distinguishes three states for every captured metric
 * (cas-e4ea, cas-711e): a real number (0 included — "0 likes" is data), `null`
 * for "upstream never told us", and — for Instagram posts — an explicit
 * `like_and_view_counts_disabled` flag meaning the creator hid the counts.
 *
 * The UI must preserve that distinction. `readMetric` keeps 0 and returns null
 * for unknown, and `formatSocialCount` returns null rather than a placeholder
 * so callers can OMIT the stat instead of rendering a fabricated `0`.
 */
import { formatKpiValue } from './kpiStatDisplay';

/** A count as it arrives from the API: number, decimal string (BigInt columns), or unknown. */
export type SocialMetricValue = number | string | null | undefined;

/**
 * Coerce an API count to a number, preserving 0 and rejecting unknown/garbage.
 * BigInt-backed columns (follower counts) arrive as decimal strings.
 */
export function toSocialCount(value: SocialMetricValue): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : null;
}

/**
 * Compact display for a social count — `104419077` → `"104.4M"`, `12345` → `"12.3K"`.
 * Returns null when the count is unknown so the caller omits the stat entirely;
 * never substitutes 0 or a placeholder for missing data.
 */
export function formatSocialCount(value: SocialMetricValue): string | null {
  const numeric = toSocialCount(value);
  if (numeric === null || numeric < 0) return null;
  return formatKpiValue(numeric);
}

/**
 * First present numeric metric across key aliases, preserving an explicit 0.
 * `convertApiResponse` camelCases payloads, so callers pass camelCase first and
 * the raw snake_case provider key as a fallback.
 */
export function readMetric(
  source: Record<string, unknown> | null | undefined,
  ...keys: string[]
): number | null {
  if (!source) return null;
  for (const key of keys) {
    const numeric = toSocialCount(source[key] as SocialMetricValue);
    if (numeric !== null) return numeric;
  }
  return null;
}

/** First present boolean flag across key aliases; null when no key carries a boolean. */
export function readFlag(
  source: Record<string, unknown> | null | undefined,
  ...keys: string[]
): boolean | null {
  if (!source) return null;
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'boolean') return value;
  }
  return null;
}
